class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(true);
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.closeTimeout = null;
    this.openTimeout = null;
    this.hoverCloseTimeout = null;
    this.closeTransitionHandler = null;
    this.openedByHover = false;
    this.animationTime = 450;
    this.hoverMediaQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
  }

  isDesktopHoverEnabled() {
    return this.hoverMediaQuery.matches;
  }

  getOpenedMenus() {
    return [...document.querySelectorAll('header-menu')].filter((menu) => {
      return menu !== this && menu.mainDetailsToggle && menu.mainDetailsToggle.hasAttribute('open');
    });
  }

  openMenu(openedMenus, animated = true) {
    openedMenus.forEach((menu) => menu.close(animated));

    window.clearTimeout(this.openTimeout);

    if (!animated) {
      this.open();
      return;
    }

    this.openTimeout = window.setTimeout(() => this.open(), openedMenus.length ? 80 : 0);
  }

  removeCloseTransitionListener() {
    if (!this.closeTransitionHandler) return;

    this.content.removeEventListener('transitionend', this.closeTransitionHandler);
    this.closeTransitionHandler = null;
  }

  onMouseEnter() {
    if (!this.isDesktopHoverEnabled()) return;
    window.clearTimeout(this.hoverCloseTimeout);

    if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.classList.contains('is-closing')) {
      return;
    }

    this.openedByHover = true;
    this.openMenu(this.getOpenedMenus(), false);
  }

  onMouseLeave() {
    if (!this.isDesktopHoverEnabled() || !this.openedByHover) return;
    window.clearTimeout(this.hoverCloseTimeout);

    this.hoverCloseTimeout = window.setTimeout(() => {
      if (this.matches(':hover')) return;
      this.close(true);
    }, 40);
  }

  onSummaryClick(event) {
    event.preventDefault();
    window.clearTimeout(this.hoverCloseTimeout);

    if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.classList.contains('is-closing')) {
      this.close(true);
      return;
    }

    this.openedByHover = false;
    this.openMenu(this.getOpenedMenus());
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  open() {
    window.clearTimeout(this.closeTimeout);
    window.clearTimeout(this.openTimeout);
    window.clearTimeout(this.hoverCloseTimeout);
    this.removeCloseTransitionListener();

    this.mainDetailsToggle.classList.remove('is-open', 'is-closing', 'is-opening');
    this.mainDetailsToggle.classList.add('is-opening');
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);

    // En la primera apertura, el navegador puede crear el panel y aplicar el estado final
    // en el mismo frame. is-opening deja el panel en estado inicial sin transición,
    // y el doble RAF fuerza un frame cerrado antes de animar hacia abierto.
    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.content.getBoundingClientRect();

      requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.remove('is-opening');
        this.mainDetailsToggle.classList.add('is-open');
      });
    });
  }

  close(animated = false) {
    window.clearTimeout(this.openTimeout);
    window.clearTimeout(this.closeTimeout);
    window.clearTimeout(this.hoverCloseTimeout);
    this.removeCloseTransitionListener();
    this.openedByHover = false;

    this.summary.setAttribute('aria-expanded', false);

    if (!animated || !this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.classList.remove('is-open', 'is-closing', 'is-opening');
      this.mainDetailsToggle.removeAttribute('open');
      return;
    }

    this.mainDetailsToggle.classList.remove('is-open');
    this.mainDetailsToggle.classList.add('is-closing');

    // También forzamos reflow al cerrar para evitar cierres instantáneos intermitentes.
    this.content.getBoundingClientRect();

    const finishClose = () => {
      this.mainDetailsToggle.classList.remove('is-closing', 'is-opening');
      this.mainDetailsToggle.removeAttribute('open');
    };

    const onTransitionEnd = (event) => {
      if (event.target !== this.content || event.propertyName !== 'transform') return;
      this.removeCloseTransitionListener();
      window.clearTimeout(this.closeTimeout);
      finishClose();
    };

    this.closeTransitionHandler = onTransitionEnd;
    this.content.addEventListener('transitionend', onTransitionEnd);

    // Fallback por si transitionend no dispara por cambio de pestaña, navegador, etc.
    this.closeTimeout = window.setTimeout(() => {
      this.removeCloseTransitionListener();
      finishClose();
    }, this.animationTime + 80);
  }
}

customElements.define('header-menu', HeaderMenu);
